import { forwardRef, type FormEvent, type MouseEvent } from "react";

import { useFormikContext } from "formik";
import { equals } from "ramda";

import { Button, ButtonProps } from "src/components/Button";

export interface FormikButtonProps extends ButtonProps {
  /** Override the auto-computed disabled state. */
  disabled?: boolean;
}

const FormikButton = forwardRef<HTMLButtonElement, FormikButtonProps>(
  ({ disabled, ...otherProps }, ref) => {
    const { handleSubmit, isSubmitting, values, initialValues, isValid } =
      useFormikContext();

    const isDisabled =
      disabled ?? (isSubmitting || equals(values, initialValues));

    // Formik's handleSubmit calls preventDefault on the event it receives, which
    // cancels the native form submission of a type="submit" button. Dropping the
    // event lets that native submit run alongside this one, submitting twice.
    const handleClick = (
      event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
    ) => handleSubmit(event as unknown as FormEvent<HTMLFormElement>);

    return (
      <Button
        ref={ref}
        disabled={isDisabled}
        loading={isSubmitting && isValid}
        onClick={handleClick}
        {...otherProps}
      />
    );
  }
);

FormikButton.displayName = "FormikButton";

export { FormikButton };
